import moment from 'moment-timezone'
import { get, map } from 'lodash'
import * as directOfferConstants from '../constants/directOffer'
import { nextBusinessDay, API_FULL_DATETIME_FORMAT } from '../../util/date'
import { absoluteToRelativeUrl } from '../../util/url'
import * as directOfferFixtures from './fixtures'

export default class DirectOffer {
  constructor(directOffer) {
    this._direct_offer = directOffer
  }

  static fixtures = directOfferFixtures

  static constants = directOfferConstants

  get offerId() {
    return this._direct_offer.offer_id
  }

  get offerStatus() {
    return this._direct_offer.offer_status
  }

  get offerType() {
    return this._direct_offer.offer_type
  }

  get offerExpirationDate() {
    return this._direct_offer.offer_expiration_date
  }

  get amount() {
    return this._direct_offer.amount
  }

  get previousAmount() {
    return this._direct_offer.previous_amount
  }

  get buyersPremium() {
    return this._direct_offer.buyers_premium
  }

  get earnestMoneyDeposit() {
    return this._direct_offer.earnest_money_deposit
  }

  get activities() {
    return this._direct_offer.activities
  }

  get property() {
    return get(this._direct_offer, 'property', {})
  }

  get isPending() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.PENDING
  }

  get isCounterOffer() {
    return this.offerType === DirectOffer.constants.OFFER_TYPES.COUNTER
  }

  get isCounterOfferPending() {
    return this.isPending && this.isCounterOffer
  }

  get isAccepted() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.ACCEPTED
  }

  get isDeclined() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.DECLINED
  }

  get isCanceled() {
    return (
      this.offerStatus === DirectOffer.constants.OFFER_STATUSES.SELLER_CANCELED
    )
  }

  get isFallout() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.FALLOUT
  }

  /*
  Indicates whether this offer should prevent the associated buyer from
  submitting another offer on the same property.
   */
  get isBlockingFurtherOffers() {
    return ![
      DirectOffer.constants.OFFER_STATUSES.DECLINED,
      DirectOffer.constants.OFFER_STATUSES.EXPIRED,
      DirectOffer.constants.OFFER_STATUSES.WITHDRAWN,
      DirectOffer.constants.OFFER_STATUSES.AUTO_DECLINE,
    ].includes(this.offerStatus)
  }

  get offerAcceptedDate() {
    return moment(
      this.getActivityCreatedDate(
        DirectOffer.constants.ACTIVITY_TYPES.ACCEPTED
      ),
      API_FULL_DATETIME_FORMAT
    )
  }

  get contractDeadline() {
    return nextBusinessDay(this.offerAcceptedDate.add(1, 'days'))
  }

  get isPastContractDeadline() {
    const now = moment()
    return now.isAfter(this.contractDeadline)
  }

  get isOfferAutoDeclined() {
    return (
      this.offerStatus === DirectOffer.constants.OFFER_STATUSES.AUTO_DECLINE
    )
  }

  get isDeclinedByBuyer() {
    return (
      this.isDeclined &&
      this.getActivityUserType(
        DirectOffer.constants.ACTIVITY_TYPES.DECLINED
      ) === DirectOffer.constants.USER_TYPES.BUYER
    )
  }

  get isDeclinedBySeller() {
    return (
      this.isDeclined &&
      this.getActivityUserType(
        DirectOffer.constants.ACTIVITY_TYPES.DECLINED
      ) === DirectOffer.constants.USER_TYPES.SELLER
    )
  }

  get isOfferExpired() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.EXPIRED
  }

  get isOfferWithdrawn() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.WITHDRAWN
  }

  get didOfferFallOut() {
    return this.offerStatus === DirectOffer.constants.OFFER_STATUSES.FALLOUT
  }

  get isOfferCanceledBySeller() {
    return (
      this.offerStatus === DirectOffer.constants.OFFER_STATUSES.SELLER_CANCELED
    )
  }

  get isOfferReviewedBySeller() {
    return (
      this.activities &&
      map(this.activities, 'activity_type').includes(
        DirectOffer.constants.ACTIVITY_TYPES.REVIEWED_BY_SELLER
      )
    )
  }

  get isPendingOfferReviewedBySeller() {
    return this.isPending && this.isOfferReviewedBySeller
  }

  get contractUrl() {
    return absoluteToRelativeUrl(
      get(this._direct_offer, 'biddingStatus.open_contract_url', null)
    )
  }

  /*
  As soon as a direct offer is accepted, the property is clerked, and an
  open_contract_url is generated. There is no delay, unlike with online auction
  properties, which depend on a cron job to be clerked.
   */
  get isContractCompleted() {
    return !this.contractUrl
  }

  get suggestedOffer() {
    return [
      this._direct_offer.suggested_offer_low,
      this._direct_offer.suggested_offer_high,
    ]
  }

  get isContractPastDue() {
    return this.isPastContractDeadline && !this.isContractCompleted
  }

  getActivity(activityType) {
    return (
      this._direct_offer.activities &&
      this._direct_offer.activities.find(
        activity => activity.activity_type === activityType
      )
    )
  }

  getActivityCreatedDate(activityType) {
    return get(this.getActivity(activityType), 'created_date', '')
  }

  getActivityUserType(activityType) {
    return get(this.getActivity(activityType), 'user_type', '')
  }
}
